import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { createPortal } from "react-dom";
import { Form } from "react-bootstrap";
import { stringToDate } from "../../utils";
import styles from "./unified-datepicker.module.scss";

const PopperContainer = ({ children }: { children: React.ReactNode }) =>
  createPortal(children, document.body);

type DateMode = "date" | "datetime" | "time";
type OutputFormat = "date" | "timestamp";

interface UnifiedDateTimePickerProps {
  // Mode configuration
  mode?: DateMode;

  // Common props
  name?: string;
  value?: Date | number | string | null | undefined;
  onChange: (value: any) => void;
  onBlur?: any;
  disabled?: boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;

  // Label & Validation (for 'date' mode)
  label?: string;
  showRequired?: boolean;
  error?: string;
  touched?: boolean;

  // DateTime specific (for 'datetime' mode)
  showTimeSelect?: boolean;
  timeIntervals?: number;
  timeAdded?: number; // Min minutes from now
  dateLabel?: string;
  timeLabel?: string;

  // Layout options
  fromSimulive?: boolean;
  width?: string;

  // Output format
  outputFormat?: OutputFormat;

  // Popper placement overrides
  timePickerPlacement?: "top-start" | "bottom-start" | "top-end" | "bottom-end";
  datePickerPlacement?: "top-start" | "bottom-start" | "top-end" | "bottom-end";

  // When true, disables past-time filtering so any time can be selected
  disableTimeFilter?: boolean;

  // Date shortcuts (for 'date' mode)
  showShortcuts?: boolean;
  shortcuts?: Array<{ label: string; value: string }>;
  isContractEndDate?: boolean;
  contractStartDate?: Date;
  startDate?: Date;
  endDate?: Date;
}

export const UnifiedDateTimePicker: React.FC<UnifiedDateTimePickerProps> = ({
  mode = "date",
  name,
  value,
  onChange,
  onBlur,
  disabled = false,
  placeholder = "yyyy-mm-dd",
  minDate,
  maxDate,
  label,
  showRequired = false,
  error,
  touched,
  showTimeSelect = true,
  timeIntervals = 15,
  timeAdded = 25,
  dateLabel,
  timeLabel,
  fromSimulive = false,
  width,
  outputFormat = "date",
  timePickerPlacement = "top-start",
  datePickerPlacement = "top-start",
  disableTimeFilter = false,
  showShortcuts = false,
  shortcuts,
  isContractEndDate = false,
  contractStartDate,
  startDate,
  endDate,
  ...props
}) => {
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const defaultTime = new Date(now.getTime() + timeAdded * 60 * 1000);

  // Initialize state based on mode and value
  const initializeDate = (): Date | null => {
    if (!value) return mode === "datetime" ? (minDate ?? startOfToday) : null;

    if (typeof value === "number") {
      return new Date(value);
    } else if (typeof value === "string") {
      return stringToDate(value, "yyyy-MM-dd");
    } else if (value instanceof Date) {
      return value;
    }
    return null;
  };

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    initializeDate()
  );
  const [selectedTime, setSelectedTime] = useState<Date>(
    value && typeof value === "number" ? new Date(value) : defaultTime
  );
  const [isOpen, setIsOpen] = useState(false);

  const datePickerRef = useRef<DatePicker>(null);
  const timePickerRef = useRef<DatePicker>(null);

  // Update state when value prop changes
  useEffect(() => {
    if (value) {
      const newDate = initializeDate();
      setSelectedDate(newDate);
      if (mode === "datetime" && newDate) {
        setSelectedTime(newDate);
      }
    }
  }, [value]);

  const minAllowedDate =
    minDate ?? startDate ?? (mode === "datetime" ? startOfToday : undefined);
  const maxAllowedDate =
    maxDate ||
    endDate ||
    (mode === "datetime"
      ? new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
      : undefined);

  const filterDateNotBeforeMin = (date: Date): boolean => {
    if (!minAllowedDate) return true;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const minDay = new Date(minAllowedDate);
    minDay.setHours(0, 0, 0, 0);
    return d.getTime() >= minDay.getTime();
  };

  // Time validation for datetime mode
  const isTimeInPast = (time: Date): boolean => {
    if (!selectedDate) return false;
    const current = new Date();
    if (selectedDate.toDateString() === current.toDateString()) {
      const currentMinutes = current.getHours() * 60 + current.getMinutes();
      const selectedMinutes = time.getHours() * 60 + time.getMinutes();
      return selectedMinutes <= currentMinutes;
    }
    return false;
  };

  // Combine date and time for datetime mode
  const combineDateAndTime = (date: Date, time: Date): number => {
    const combined = new Date(date);
    combined.setHours(time.getHours());
    combined.setMinutes(time.getMinutes());
    combined.setSeconds(0);
    combined.setMilliseconds(0);
    return combined.getTime();
  };

  // Effect to notify parent of changes in datetime mode
  useEffect(() => {
    if (mode === "datetime" && selectedDate) {
      const timestamp = combineDateAndTime(selectedDate, selectedTime);
      if (outputFormat === "timestamp") {
        onChange(timestamp);
      } else {
        onChange(new Date(timestamp));
      }
    }
  }, [selectedDate, selectedTime, mode]);

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    if (mode === "date") {
      setSelectedDate(date);
      if (outputFormat === "timestamp") {
        onChange(date ? date.getTime() : null);
      } else {
        onChange(date);
      }
      setIsOpen(false);
    } else if (mode === "datetime") {
      if (date) {
        if (!filterDateNotBeforeMin(date)) return;
        setSelectedDate(date);
        // If selecting today and current time is in the past, adjust time
        if (
          date.toDateString() === now.toDateString() &&
          isTimeInPast(selectedTime)
        ) {
          const newTime = new Date(now.getTime() + timeAdded * 60 * 1000);
          setSelectedTime(newTime);
        }
      }
    }
  };

  // Handle time change
  const handleTimeChange = (date: Date | null) => {
    if (date && !isTimeInPast(date)) {
      setSelectedTime(date);
    }
  };

  // Handle predefined date shortcuts
  const handlePredefinedDateSet = (dateModifier: string) => {
    const baseDate =
      isContractEndDate && contractStartDate ? contractStartDate : new Date();
    const modifier = dateModifier.match(/[+-]?\d+[DMY]/);

    if (!modifier) {
      console.error("Invalid date modifier");
      return;
    }

    const value = parseInt(modifier[0].slice(0, -1), 10);
    const unit = modifier[0].slice(-1);
    const newDate = new Date(baseDate);

    switch (unit) {
      case "D":
        newDate.setDate(baseDate.getDate() + value);
        break;
      case "M":
        newDate.setMonth(baseDate.getMonth() + value);
        break;
      case "Y":
        newDate.setFullYear(baseDate.getFullYear() + value);
        break;
      default:
        return;
    }

    setSelectedDate(newDate);
    if (outputFormat === "timestamp") {
      onChange(newDate.getTime());
    } else {
      onChange(newDate);
    }
    setIsOpen(false);
  };

  // Open pickers
  const openDatePicker = () => {
    if (!disabled) {
      if (mode === "date") {
        setIsOpen(true);
      } else {
        datePickerRef.current?.setOpen(true);
      }
    }
  };

  const openTimePicker = () => {
    timePickerRef.current?.setOpen(true);
  };

  const handleCalendarClick = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  // Render date mode
  if (mode === "date") {
    return (
      <div className="position-relative">
        <div className="d-flex align-items-center gap-1 p-1">
          <div className="d-flex justify-content-between align-items-center w-100">
            <span>
              <label>{label}</label>
              {showRequired && <span className="required text-danger">*</span>}
            </span>
            {isContractEndDate && (
              <span className="d-flex gap-3 small">
                <span
                  onClick={() => handlePredefinedDateSet("+30D")}
                  className="pointer"
                >
                  +30D
                </span>
                <span
                  onClick={() => handlePredefinedDateSet("+1Y")}
                  className="pointer"
                >
                  +1Y
                </span>
                <span
                  onClick={() => handlePredefinedDateSet("+2Y")}
                  className="pointer"
                >
                  +2Y
                </span>
                <span
                  onClick={() => handlePredefinedDateSet("+3Y")}
                  className="pointer"
                >
                  +3Y
                </span>
              </span>
            )}
          </div>
        </div>
        <div
          className="input-group position-relative"
          onClick={handleCalendarClick}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        >
          <DatePicker
            ref={datePickerRef}
            open={isOpen}
            selected={selectedDate}
            onChange={handleDateChange}
            onCalendarClose={() => setIsOpen(false)}
            onBlur={onBlur}
            placeholderText={placeholder}
            className={`form-control ${error && touched ? "is-invalid" : ""} ${
              styles.inputClass
            }`}
            disabled={disabled}
            dateFormat="yyyy-MM-dd"
            minDate={minAllowedDate}
            maxDate={maxAllowedDate}
            wrapperClassName={styles.wrapper}
            shouldCloseOnSelect={true}
            {...props}
          />
          <span
            className="position-absolute end-0 top-0 d-flex align-items-center justify-content-center px-2"
            style={{
              height: "100%",
              cursor: disabled ? "not-allowed" : "pointer",
              pointerEvents: "none",
            }}
          >
            {!error && <i className="bi bi-calendar-event px-2 pointer"></i>}
          </span>
        </div>
        {touched && error && (
          <div className="form-text text-danger px-2">{error}</div>
        )}
      </div>
    );
  }

  // Render datetime mode
  if (mode === "datetime") {
    return (
      <div
        className="date-time-picker row"
        style={{ position: "relative", zIndex: 1 }}
      >
        <style>
          {`
            .date-time-picker {
              position: relative;
            }
            .react-datepicker-popper {
              z-index: 999999 !important;
            }
            .react-datepicker {
              z-index: 999999 !important;
            }
            .react-datepicker-wrapper {
              width: 100%;
              position: relative;
            }
            .react-datepicker__portal {
              z-index: 999999 !important;
            }
            ${
              width
                ? `
              .form-control.datePic {
                width: 100%;
                min-width: ${width};
              }
            `
                : ""
            }
          `}
        </style>
        <Form.Group
          controlId="datePicker"
          className={fromSimulive ? "col-md-6" : "col-md-5"}
        >
          <div className="position-relative">
            {fromSimulive && dateLabel ? <label className="p-1">{dateLabel}</label> : null}
            <div className="position-relative">
              <i
                className="bi bi-calendar-date position-absolute"
                style={{
                  left: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 5,
                  color: "#6c757d",
                  fontSize: "16px",
                  cursor: "pointer",
                  pointerEvents: "auto",
                }}
                onClick={openDatePicker}
              />
              <DatePicker
                ref={datePickerRef}
                selected={selectedDate}
                onChange={handleDateChange}
                minDate={minAllowedDate}
                maxDate={maxAllowedDate}
                filterDate={filterDateNotBeforeMin}
                dateFormat="dd/MM/yyyy"
                className="form-control datePic"
                disabled={disabled}
                popperPlacement={datePickerPlacement}
                popperContainer={PopperContainer}
              />
            </div>
          </div>
        </Form.Group>
        {showTimeSelect && (
          <Form.Group
            controlId="timePicker"
            className={
              fromSimulive
                ? "col-md-6 position-relative"
                : "col-md-5 position-relative"
            }
          >
            {fromSimulive && timeLabel ? <label className="p-1">{timeLabel}</label> : null}
            <div className="position-relative">
              <i
                className="bi bi-clock-fill position-absolute"
                style={{
                  left: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 5,
                  color: "#6c757d",
                  fontSize: "16px",
                  cursor: "pointer",
                  pointerEvents: "auto",
                }}
                onClick={openTimePicker}
              />
              <DatePicker
                ref={timePickerRef}
                selected={selectedTime}
                onChange={handleTimeChange}
                showTimeSelect={showTimeSelect}
                showTimeSelectOnly
                timeIntervals={timeIntervals}
                timeCaption="Time"
                dateFormat="h:mm aa"
                className="form-control datePic"
                filterTime={(time) => !isTimeInPast(time)}
                disabled={disabled}
                popperPlacement={timePickerPlacement}
                popperContainer={PopperContainer}
              />
            </div>
          </Form.Group>
        )}
      </div>
    );
  }

  // Render time mode (if needed in the future)
  if (mode === "time") {
    return (
      <div className="position-relative">
        <DatePicker
          ref={timePickerRef}
          selected={selectedTime}
          onChange={handleTimeChange}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={timeIntervals}
          timeCaption="Time"
          dateFormat="h:mm aa"
          className={`form-control ${styles.inputClass}`}
          disabled={disabled}
        />
      </div>
    );
  }

  return null;
};

export default UnifiedDateTimePicker;
